import { resourceType } from 'constants/loading';
import { findResource } from 'loading/finders';
import { treatErrorAs } from 'api/errors';
import { errorLevel } from 'constants/errors';

import {
  getAffiliateList,
  getJohnDeereFields,
  getJohnDeereWorkPlansForActiveField,
} from 'affiliates/selectors';
import {
  fetchAffiliateList,
  fetchJohnDeereFields,
  fetchJohnDeereWorkPlans,
} from 'affiliates/loaders';
import {
  processAffiliateList,
  processJohnDeereFields,
  processJohnDeereWorkPlans,
} from 'affiliates/processors';
import {
  parseAffiliateList,
  parseJohnDeereFields,
  parseJohnDeereWorkPlans,
} from 'affiliates/affiliatesParser';

import { getActiveUserId } from 'current/selectors';
import { getPlantingScriptsIdsForActiveField } from 'scripts/planting-selectors';
import { Store } from 'store/types/types';
import { AffiliateList, DeereWorkPlans, RawJohnDeereFields } from './types';

export const findAffiliateList = ({
  dispatch,
  getState,
  forceReload = false,
  forceWait = false,
  onError,
}: {
  dispatch: () => void;
  getState: () => Store;
  forceReload?: boolean;
  forceWait?: boolean;
  onError?: unknown;
}) => {
  const userId = getActiveUserId(getState());
  const resourceSpec = {
    type: resourceType.AFFILIATE_LIST,
    selector: () => getAffiliateList(getState()),
    fetcher: () => fetchAffiliateList({ userId }),
    processor: (raw: AffiliateList) =>
      processAffiliateList({
        dispatch,
        affiliateList: parseAffiliateList(raw),
      }),
  };

  return findResource({
    dispatch,
    getState,
    forceReload,
    forceWait,
    onError,
    ...resourceSpec,
  });
};

export const findJohnDeereFields = ({
  dispatch,
  getState,
  forceReload = false,
  forceWait = false,
}: {
  dispatch: () => void;
  getState: () => Store;
  forceReload?: boolean;
  forceWait?: boolean;
}) => {
  const state = getState();
  const userId = getActiveUserId(state);
  const fvFields = state.fields.byUuid;
  const resourceSpec = {
    type: resourceType.JOHN_DEERE_FIELDS,
    selector: () => getJohnDeereFields(state),
    fetcher: () => fetchJohnDeereFields({ userId }),
    processor: (raw: RawJohnDeereFields) =>
      processJohnDeereFields({
        dispatch,
        johnDeereFields: parseJohnDeereFields(raw, fvFields),
      }),
  };

  return findResource({
    dispatch,
    getState,
    forceReload,
    forceWait,
    ...resourceSpec,
    // eslint-disable-next-line
  } as any);
};

export const findJohnDeereWorkPlans = ({
  dispatch,
  getState,
  forceReload = true,
  forceWait = false,
}: {
  dispatch: () => void;
  getState: () => Store;
  forceReload?: boolean;
  forceWait?: boolean;
}) => {
  const state = getState();
  const userId = getActiveUserId(state);
  const scriptIds = getPlantingScriptsIdsForActiveField(state);
  const onError = treatErrorAs({ severity: errorLevel.INFO } as unknown);
  const resourceSpec = {
    type: resourceType.JOHN_DEERE_WORK_PLANS,
    selector: () => getJohnDeereWorkPlansForActiveField(state),
    fetcher: () => fetchJohnDeereWorkPlans({ userId, scriptIds }),
    processor: (raw: DeereWorkPlans) =>
      processJohnDeereWorkPlans({
        dispatch,
        johnDeereWorkPlans: parseJohnDeereWorkPlans(raw),
      }),
  };
  return findResource({
    dispatch,
    getState,
    forceReload,
    forceWait,
    onError,
    ...resourceSpec,
  });
};
